import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import React, { useCallback, useEffect, useState, useContext } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BetaTag } from 'ui-components/components/beta-tag';
import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { InteractablePopover } from 'ui-components/components/interactable-popover';
import { darkTooltipOnly, POPOVER_TOOLTIP } from 'ui-components/components/interactable-popover/constants';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { emptyObject, emptyArray } from 'ui-components/default-props/empty';

import style from './edit-delivery-option-page.module.less';
import { FallbackOptions } from './fallback-options';
import LiveRateAdjustments from './live-rate-adjustments';
import PickupSettingsSchedule from './pickup-settings-schedule';
import useLocalDeliveryServicesAvailableForSeller from './use-local-delivery-services-available-for-seller';
import useNonLocalDeliveryServicesAvailableForSeller from './use-non-local-delivery-services-available-for-seller';
import { WeightBreakpoint } from './weight-breakpoint';
import { WeightBreakpointReadOnly } from './weight-breakpoint-read-only';
import { CarrierServiceSelector } from '../../../../../../components/carrier-service-selector';
import { CarrierServiceTagBank } from '../../../../../../components/carrier-service-tag-bank';

import { SimpleModal } from '../../../../../../components/simple-modal/simple-modal';
import { useModal } from '../../../../../../hooks/use-modal';
import useProviderOrCarrierGroups from '../../../../../../components/shipping-service-selector/group-hooks/use-provider-or-carrier-groups';
import { linkIDs } from '../../../../../../services/link-service/links';
import Global from '../../../../../../store/global-selectors';
import HelpArticleLink from '../../../../../../components/help-article-link';
import messages from '../../messages';
import { BY_CUSTOM_PACKAGE, BY_VOLUME, SKIP_DIMENSIONS, ALL_CALC_TYPE, BY_WEIGHT_CALC_TYPE } from '../constants';
import { DeliveryOptionsContext } from '../context';
import DeliveryOptionsItemPropType from '../delivery-option-item-prop-type';
import { pricingModelEnum } from '../enums';
import { useCalculationMethodType } from '../hooks';
import { ConfirmDialogueModal } from '../../../../../../components/modals/confirm-dialogue-modal';

export const LiveRateOptions = ({
  additionalFees = emptyObject,
  fallbackOption = emptyObject,
  fallbackOptionObject = emptyObject,
  fallbackOptions = emptyArray,
  services = emptyArray,
  item,
  setItem,
  pricingMode = '',
}) => {
  const { sharedDefaultDimensionsByWeight } = useContext(DeliveryOptionsContext);
  const allNonLocalDeliveryServices = useNonLocalDeliveryServicesAvailableForSeller();
  const allLocalDeliveryServices = useLocalDeliveryServicesAvailableForSeller();
  const isLocalDeliveryModeSelected = pricingMode === pricingModelEnum.localDeliveryRate;
  const availableServices = isLocalDeliveryModeSelected ? allLocalDeliveryServices : allNonLocalDeliveryServices;
  const sellerHasOnlyOneLocalDeliveryServiceAndLocalDeliveryModeIsSelected =
    isLocalDeliveryModeSelected && allLocalDeliveryServices.length === 1;
  const singleLocalDeliveryServiceId = allLocalDeliveryServices.length ? parseInt(allLocalDeliveryServices[0].serviceId, 10) : '';
  const singleLocalDeliverySellerLabelProviderId = allLocalDeliveryServices.length ? allLocalDeliveryServices[0].sellerLabelProviderId : '';
  const providerOrCarrierGroups = useProviderOrCarrierGroups(availableServices || emptyArray, null, true);
  const accounts = useSelector(Global.selectSellerLabelProviders);

  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const calcMethodType = useCalculationMethodType();
  const showCalcButtons = calcMethodType !== BY_WEIGHT_CALC_TYPE;
  const sellerPackages = useSelector(Global.selectSellerPackages) || [];
  const customPackages = sellerPackages.filter((p) => p.isCustom);
  const binPackingIsDisabled = customPackages.length === 0;
  const [localBackupDimensions, setLocalBackupDimensions] = useState(item?.defaultDimensionsByWeightList);
  const history = useHistory();
  const { close, isOpen, open } = useModal();
  const { open: onOpenConfirmModal, close: onCloseConfirmModal, isOpen: isConfirmModalOpen } = useModal();

  const findZeroValues = (backupItem, index) => {
    let isZero = false;
    const { width } = backupItem.defaultDimensions;
    const { length } = backupItem.defaultDimensions;
    const { height } = backupItem.defaultDimensions;
    const weight = backupItem.maxWeight.value;
    const zeroDimValue = width === 0 || length === 0 || height === 0;
    const zeroWeightValue = weight === 0 && index !== localBackupDimensions.length - 1;
    if (zeroDimValue || zeroWeightValue) {
      isZero = true;
    }
    return isZero;
  };

  const hasZeroValues = item?.defaultDimensionsByWeightList.filter((bui, index) => {
    return findZeroValues(bui, index) === true;
  });

  const weights = localBackupDimensions.map((bud) => bud.maxWeight.value);
  const hasWeightErrors = weights.some((e, i) => (i ? e <= weights[i - 1] : false));

  const isDisabled =
    localBackupDimensions.filter((bui, index) => {
      return findZeroValues(bui, index) === true;
    }).length > 0 || hasWeightErrors;

  const serviceHash = providerOrCarrierGroups.reduce((serviceHashList, group) => {
    const updatedServiceListItems = group.items.reduce((acc, grpItem) => {
      const { sellerLabelProviderId, serviceId } = grpItem;
      const lookupId = `${sellerLabelProviderId}-${serviceId}`;
      return {
        ...acc,
        [lookupId]: grpItem,
      };
    }, {});
    return {
      ...serviceHashList,
      ...updatedServiceListItems,
    };
  }, {});

  const chosenServices = services.reduce((mappedServices, service) => {
    const { sellerProviderId, serviceId } = service;
    const lookupId = `${sellerProviderId}-${serviceId}`;
    const foundServiceData = serviceHash[lookupId];

    if (foundServiceData) {
      const accountDetails = accounts.find((account) => account.sellerLabelProviderId === sellerProviderId.toString());
      const foundIndex = mappedServices.findIndex(
        (ms) => ms.sellerLabelProviderId === foundServiceData.sellerLabelProviderId && ms.serviceId === foundServiceData.serviceId,
      );
      const foundDataWithNickname = {
        ...foundServiceData,
        accountNickname: accountDetails?.name,
      };
      if (foundIndex < 0) {
        return [...mappedServices, foundDataWithNickname];
      }
    }
    return mappedServices;
  }, []);

  const [unsavedServices, setUnsavedServices] = useState(chosenServices || emptyArray);

  const getCalculationMethod = () => {
    // changes the calculation method to by volume in the event a user previously had custom packages and
    // chose bin packing but then deleted all custom packages
    if (binPackingIsDisabled && item.calculationMethod === BY_CUSTOM_PACKAGE) {
      setItem((prev) => ({
        ...prev,
        calculationMethod: BY_VOLUME,
      }));
      return BY_VOLUME;
    }
    return item.calculationMethod;
  };

  const [calculationMethod, setCalculationMethod] = useState(() => getCalculationMethod());

  const onChangeCalculationMethod = useCallback(
    (calcMethod) => {
      if (calcMethodType === ALL_CALC_TYPE && item.defaultDimensionsByWeightList.length === 0) {
        setItem({
          ...item,
          defaultDimensionsByWeightList: sharedDefaultDimensionsByWeight,
        });
        setLocalBackupDimensions(sharedDefaultDimensionsByWeight);
      }
      setCalculationMethod(calcMethod);
      setItem((prev) => ({
        ...prev,
        calculationMethod: calcMethod,
      }));
    },
    [calcMethodType, item, setItem, sharedDefaultDimensionsByWeight],
  );

  const {
    originAddress: { ianaTimeZone: defaultShipFromTimeZone },
    pickupSettings: defaultShipFromPickupSettings,
    shipFromId: defaultShipFromId,
  } = useSelector(Global.selectSellerDefaultShipFrom);

  const onAddServiceClickCallback = useCallback(
    ({ serviceId, sellerLabelProviderId }) => {
      setItem({
        ...item,
        services: [
          ...item.services,
          {
            serviceId,
            sellerProviderId: sellerLabelProviderId,
          },
        ],
      });
    },
    [setItem, item],
  );

  const onConfirmServicesCallback = useCallback(
    (addedServices) => {
      const servicesWithNicknames = addedServices.map((serviceToAdd) => {
        const accountDetails = accounts.find((account) => account.sellerLabelProviderId === serviceToAdd.sellerLabelProviderId.toString());

        if (!serviceToAdd.accountNickname && accountDetails) {
          return {
            ...serviceToAdd,
            accountNickname: accountDetails.name,
          };
        }
        return serviceToAdd;
      });

      setUnsavedServices(servicesWithNicknames);
      const updatedServices = servicesWithNicknames.map((service) => {
        return {
          serviceId: service.serviceId,
          sellerProviderId: service.sellerLabelProviderId,
        };
      });
      setItem({
        ...item,
        services: [...updatedServices],
      });
    },
    [setItem, item, accounts],
  );

  const onServicesCleared = useCallback(() => {
    setUnsavedServices([]);
    setItem({
      ...item,
      services: [],
    });
  }, [item, setItem]);

  const deleteBreakpoint = useCallback(
    (index) => {
      const breakpointList = [...localBackupDimensions];
      breakpointList.splice(index, 1);
      setLocalBackupDimensions(breakpointList);
    },
    [localBackupDimensions],
  );

  const onCancel = useCallback(() => {
    onOpenConfirmModal();
  }, [onOpenConfirmModal]);

  const onConfirmCancel = useCallback(() => {
    setLocalBackupDimensions(item?.defaultDimensionsByWeightList);
    onCloseConfirmModal();
    close();
  }, [close, item?.defaultDimensionsByWeightList, onCloseConfirmModal]);

  const onSaveBackupDimensions = useCallback(() => {
    setItem({
      ...item,
      defaultDimensionsByWeightList: [...localBackupDimensions],
    });
    close();
  }, [close, item, localBackupDimensions, setItem]);

  const onChangeLocalPackageDimensions = useCallback(
    (dimensions, index) => {
      const newHeight = dimensions.height || 0;
      const newWidth = dimensions.width || 0;
      const newLength = dimensions.length || 0;
      setLocalBackupDimensions(
        localBackupDimensions.map((localDims, i) => {
          if (index !== i) {
            return localDims;
          }
          return {
            maxWeight: localDims.maxWeight,
            defaultDimensions: {
              height: newHeight,
              width: newWidth,
              length: newLength,
              unit: sellerDefaultDimensionUnit,
            },
          };
        }),
      );
    },
    [localBackupDimensions, sellerDefaultDimensionUnit],
  );

  const onChangeLocalPackageWeight = useCallback(
    (weight, index) => {
      const newWeight = weight || 0;
      setLocalBackupDimensions(
        localBackupDimensions.map((localDims, i) => {
          if (index !== i) {
            return localDims;
          }
          return {
            maxWeight: {
              value: newWeight,
              unit: sellerDefaultWeightUnit,
            },
            defaultDimensions: localDims.defaultDimensions,
          };
        }),
      );
    },
    [localBackupDimensions, sellerDefaultWeightUnit],
  );

  const onCarrierServiceRemoved = useCallback(
    (serviceId, sellerLabelProviderId) => {
      const checkedServices = services.filter(
        (service) => !(service.serviceId.toString() === serviceId && service.sellerProviderId.toString() === sellerLabelProviderId),
      );
      const unsavedChosenServices = unsavedServices.filter(
        (service) => !(service.serviceId === serviceId && service.sellerLabelProviderId === sellerLabelProviderId),
      );
      setUnsavedServices(unsavedChosenServices);
      setItem({
        ...item,
        services: checkedServices,
      });
    },
    [item, services, setItem, unsavedServices],
  );

  useEffect(() => {
    if (
      sellerHasOnlyOneLocalDeliveryServiceAndLocalDeliveryModeIsSelected &&
      item.services[0]?.serviceId !== singleLocalDeliveryServiceId
    ) {
      onAddServiceClickCallback({
        serviceId: singleLocalDeliveryServiceId,
        sellerLabelProviderId: singleLocalDeliverySellerLabelProviderId,
      });
    }
  }, [
    item,
    onAddServiceClickCallback,
    sellerHasOnlyOneLocalDeliveryServiceAndLocalDeliveryModeIsSelected,
    singleLocalDeliverySellerLabelProviderId,
    singleLocalDeliveryServiceId,
  ]);

  const goToCustomPackages = useCallback(
    (e) => {
      e.stopPropagation();
      history.push('/settings/packages?activeTab=1');
    },
    [history],
  );

  const rateAdjustmentsExplanationText = isLocalDeliveryModeSelected
    ? messages.rateAdjustmentsExplanationLocalDelivery
    : messages.rateAdjustmentsExplanation;

  return (
    <>
      <div>
        <div className={style['section-container']}>
          <div className={classnames(style['live-rate-section'], style['service-dropdown-section'])}>
            <Text className={style['card-section-title']} textType={TextType.Paragraph} value={messages.services} />
            <Text className={style['section-info']} textType={TextType.Paragraph} value={messages.servicesSectionSubHeader} />
            <Text className={style['service-title']} textType={TextType.Paragraph} value={messages.selectService} />
            {sellerHasOnlyOneLocalDeliveryServiceAndLocalDeliveryModeIsSelected ? (
              <div className={style['single-local-delivery-service-name']}>{allLocalDeliveryServices[0].name}</div>
            ) : (
              <div className={style['section-controls']}>
                <CarrierServiceSelector
                  groupedCarrierServices={providerOrCarrierGroups}
                  label={messages.shippingServiceDropdownPlaceholder}
                  onServicesCleared={onServicesCleared}
                  onServicesConfirmed={onConfirmServicesCallback}
                  onSetUnsavedStrategyServices={onConfirmServicesCallback}
                  unsavedStrategyServices={unsavedServices}
                />
              </div>
            )}
          </div>
          <CarrierServiceTagBank carrierServices={unsavedServices} onCarrierServiceRemoved={onCarrierServiceRemoved} shouldAllowRemoval />
        </div>
        {isLocalDeliveryModeSelected ? (
          <>
            <div className={style['live-rate-title']}>
              <FormattedMessage {...messages.localDeliveryAvailability} />
            </div>
            <div>
              <PickupSettingsSchedule
                defaultShipFromId={defaultShipFromId}
                pickupDays={defaultShipFromPickupSettings?.days}
                shipFromTimeZone={defaultShipFromTimeZone}
              />
            </div>
          </>
        ) : null}
      </div>
      <div className={style['edit-delivery-options-section']}>
        {!isLocalDeliveryModeSelected && showCalcButtons ? (
          <div className={style['section-container']}>
            <div className={style['title-container']}>
              <FlexRow>
                <FlexRow>
                  <Text className={style['card-section-title']} textType={TextType.Paragraph} value={messages.calcMethodSectionTitle} />
                  {calcMethodType === ALL_CALC_TYPE && <BetaTag />}
                </FlexRow>
              </FlexRow>
              <div>
                <Text
                  className={style['section-info']}
                  element='span'
                  formattedMessageValues={{
                    link: (chunks) => (
                      <HelpArticleLink articleId={linkIDs.CheckoutRatesDimensionalConfiguration}>
                        {' '}
                        {chunks}
                        <FontAwesomeIcon className={style['external-link-icon']} icon={faArrowUpRightFromSquare} />{' '}
                      </HelpArticleLink>
                    ),
                  }}
                  textType={TextType.Paragraph}
                  value={messages.calcMethodSectionInfo}
                />
              </div>
              <div>
                <BannerMessageV2
                  className={style['banner-info']}
                  content={<Text textType={TextType.Caption} value={messages.sharedSettingsInfo} />}
                  layout={BannerLayout.Full}
                  status={BannerStatus.Info}
                />
              </div>
            </div>
            <div className={style['rate-type-button-container']}>
              {calcMethodType === ALL_CALC_TYPE && (
                <Button
                  className={classnames(style['calc-button'], {
                    [style.selected]: calculationMethod === SKIP_DIMENSIONS,
                  })}
                  onClick={() => onChangeCalculationMethod(SKIP_DIMENSIONS)}
                  theme='white'
                >
                  <Text className={style['rate-type-name']} textType={TextType.Paragraph} value={messages.byWeightCalcMethod} />
                  <Text className={style['rate-type-description']} textType={TextType.Paragraph} value={messages.byWeightDescription} />
                  {calculationMethod === SKIP_DIMENSIONS && <FontAwesomeIcon className={style.check} icon={faCheck} />}
                </Button>
              )}

              <Button
                className={classnames(style['calc-button'], {
                  [style.selected]: calculationMethod === BY_VOLUME,
                })}
                onClick={() => onChangeCalculationMethod(BY_VOLUME)}
                theme='white'
              >
                <Text className={style['rate-type-name']} textType={TextType.Paragraph} value={messages.simpleCalcMethod} />
                <Text className={style['rate-type-description']} textType={TextType.Paragraph} value={messages.byVolumeDescription} />
                {calculationMethod === BY_VOLUME && <FontAwesomeIcon className={style.check} icon={faCheck} />}
              </Button>
              {binPackingIsDisabled ? (
                <InteractablePopover
                  hideTooltipTitle
                  popoverBody={(popoverType) =>
                    popoverType === POPOVER_TOOLTIP ? (
                      <FormattedMessage {...messages.disabledMessage} />
                    ) : (
                      <>
                        <FormattedMessage {...messages.disabledMessage} />
                        <div className={style['popover-footer']}>
                          <Button className={style['pop-over-button']} onClick={goToCustomPackages}>
                            <FormattedMessage {...messages.setupPackages} />
                          </Button>
                          <HelpArticleLink articleId={linkIDs.CheckoutRatesDimensionalConfiguration} className={style['help-link']}>
                            <div>
                              <FormattedMessage {...messages.learnMore} />
                              <FontAwesomeIcon className={style['external-link-icon']} icon={faArrowUpRightFromSquare} />
                            </div>
                          </HelpArticleLink>
                        </div>
                      </>
                    )
                  }
                  preferPlace='bottom'
                  previewMessage={<FormattedMessage {...messages.viewMore} />}
                  shouldBeDark={darkTooltipOnly}
                  title={<FormattedMessage {...messages.enableCartonization} />}
                >
                  <div className={classnames(style['disabled-pop-over'])}>
                    <Text className={style['rate-type-name']} textType={TextType.Paragraph} value={messages.binPackingCalcMethod} />
                    <Text className={style['rate-type-description']} textType={TextType.Paragraph} value={messages.binPackingDescription} />
                  </div>
                </InteractablePopover>
              ) : null}

              {!binPackingIsDisabled && (
                <Button
                  className={classnames(style['calc-button'], {
                    [style.selected]: calculationMethod === BY_CUSTOM_PACKAGE,
                  })}
                  disabled={customPackages.length === 0}
                  onClick={() => onChangeCalculationMethod(BY_CUSTOM_PACKAGE)}
                  theme='white'
                >
                  <Text className={style['rate-type-name']} textType={TextType.Paragraph} value={messages.binPackingCalcMethod} />
                  <Text className={style['rate-type-description']} textType={TextType.Paragraph} value={messages.binPackingDescription} />
                  {calculationMethod === BY_CUSTOM_PACKAGE && <FontAwesomeIcon className={style.check} icon={faCheck} />}
                </Button>
              )}
            </div>
            {calculationMethod !== SKIP_DIMENSIONS && (
              <div className={style['dims-modal']}>
                <div className={style['edit-icon']}>
                  <FlexRow>
                    <Text textType={TextType.H4} value={messages.defaultDimensionsTitle} />
                    <Text className={style.required} element='span' textType={TextType.Paragraph} value={messages.required} />
                  </FlexRow>
                  {item?.defaultDimensionsByWeightList && hasZeroValues.length === 0 ? (
                    <Button className={style['header-text']} onClick={open} theme='link'>
                      <FontAwesomeIcon className={style['pen-icon']} icon={faPen} />
                      <Text textType={TextType.Caption} value={messages.editRange} />
                    </Button>
                  ) : null}
                </div>
                <Text
                  className={style['dimensions-section-subtitle']}
                  textType={TextType.Paragraph}
                  value={messages.defaultDimensionsInfo}
                />
                {item?.defaultDimensionsByWeightList && hasZeroValues.length !== 0 ? (
                  <Button onClick={open} theme='primary'>
                    <Text textType={TextType.Caption} value={messages.addDimensions} />
                  </Button>
                ) : null}
                {item?.defaultDimensionsByWeightList.length > 0 &&
                  item?.defaultDimensionsByWeightList.map((weightItem, index) => {
                    const isIncomplete = findZeroValues(weightItem, index);
                    if (isIncomplete) {
                      return;
                    }
                    let lastWeight = 0;

                    if (index > 0) {
                      lastWeight = item?.defaultDimensionsByWeightList[index - 1].maxWeight.value;
                    }
                    return (
                      <WeightBreakpointReadOnly
                        key={index}
                        arrayLength={item?.defaultDimensionsByWeightList.length}
                        dimensions={weightItem.defaultDimensions}
                        index={index}
                        prevWeight={lastWeight}
                        sellerDefaultDimensionUnit={sellerDefaultDimensionUnit}
                        weight={weightItem.maxWeight.value}
                      />
                    );
                  })}
              </div>
            )}
          </div>
        ) : null}
        <div className={style['section-container']}>
          <Text className={style['card-section-title']} textType={TextType.Paragraph} value={messages.rateAdjustment} />
          <div className={style['section-info']}>
            <FormattedMessage {...rateAdjustmentsExplanationText} />
            <Tooltip>
              <TooltipContent>
                <Text textType={TextType.Paragraph} value={messages.ratesAdjustmentTooltip} />
              </TooltipContent>
              <div className={style['icon-container']}>
                <FontAwesomeIcon className={style['info-icon']} icon={faInfoCircle} />
              </div>
            </Tooltip>
          </div>
          <Text className={style['adjustment-title']} textType={TextType.Paragraph} value={messages.selectTypeOfAdjustment} />
          <div className={style['live-rate-content']}>
            <LiveRateAdjustments additionalFees={additionalFees} item={item} setItem={setItem} />
          </div>
        </div>
      </div>
      {!isLocalDeliveryModeSelected && (
        <div className={style['section-container']}>
          <div>
            <Text className={style['card-section-title']} textType={TextType.Paragraph} value={messages.backupRateTitle} />
          </div>
          <div>
            <Text className={style['section-info']} textType={TextType.Paragraph} value={messages.backupRateInfo} />
          </div>
          <FallbackOptions
            fallbackOption={fallbackOption}
            fallbackOptionObject={fallbackOptionObject}
            fallbackOptions={fallbackOptions}
            item={item}
            setItem={setItem}
          />
        </div>
      )}

      {isOpen ? (
        <SimpleModal
          actionMessage={messages.updateDims}
          cancelMessage={messages.cancel}
          headerMessage={messages.defaultDimensionsModalTitle}
          isActionDisabled={isDisabled}
          onAction={onSaveBackupDimensions}
          onCancel={onCancel}
          onClose={onCancel}
          size='large'
        >
          <div className={style['modal-body']}>
            <Text textType={TextType.H4} value={messages.dimensionsModalSubTitle} />
            <div className={style['modal-info-container']}>
              <Text textType={TextType.Caption} value={messages.defaultDimensionsInfo} />
              <Text textType={TextType.Caption} value={messages.defaultDimensionsModalInfo} />
            </div>
            {localBackupDimensions.length > 0 &&
              localBackupDimensions.map((weightItem, index) => {
                let lastWeight = 0;

                if (index > 0) {
                  lastWeight = localBackupDimensions[index - 1].maxWeight.value;
                }
                return (
                  <WeightBreakpoint
                    key={index}
                    arrayLength={localBackupDimensions.length}
                    deleteBreakpoint={deleteBreakpoint}
                    dimensions={weightItem.defaultDimensions}
                    index={index}
                    item={localBackupDimensions}
                    onChangePackageDimensions={onChangeLocalPackageDimensions}
                    onChangePackageWeight={onChangeLocalPackageWeight}
                    prevWeight={lastWeight}
                    sellerDefaultDimensionUnit={sellerDefaultDimensionUnit}
                    sellerDefaultWeightUnit={sellerDefaultWeightUnit}
                    setItem={setLocalBackupDimensions}
                    weight={weightItem.maxWeight.value}
                  />
                );
              })}
            {isConfirmModalOpen ? (
              <ConfirmDialogueModal
                body={<FormattedMessage {...messages.confirmBody} />}
                buttonMessage={<FormattedMessage {...messages.continue} />}
                header={<FormattedMessage {...messages.confirmHeader} />}
                onClose={onCloseConfirmModal}
                onConfirm={onConfirmCancel}
              />
            ) : null}
          </div>
        </SimpleModal>
      ) : null}
    </>
  );
};

LiveRateOptions.propTypes = {
  additionalFees: PropTypes.shape({
    flatRate: PropTypes.shape({
      value: PropTypes.number,
    }),
    mode: PropTypes.string,
    percentage: PropTypes.number,
  }),
  fallbackOption: PropTypes.shape({
    cartPercentage: PropTypes.number,
    flatRate: PropTypes.shape({
      value: PropTypes.number,
    }),
    mode: PropTypes.string,
  }),
  fallbackOptionObject: PropTypes.shape({
    displayName: PropTypes.shape({
      defaultMessage: PropTypes.string,
    }),
  }),
  fallbackOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      displayName: PropTypes.shape({
        defaultMessage: PropTypes.string,
      }),
    }),
  ),
  services: PropTypes.arrayOf(
    PropTypes.shape({
      sellerProviderId: PropTypes.string,
      serviceId: PropTypes.string,
    }),
  ),
  item: DeliveryOptionsItemPropType.isRequired,
  setItem: PropTypes.func.isRequired,
  pricingMode: PropTypes.oneOf(Object.values(pricingModelEnum)),
};
